import { AlertTriangle } from 'lucide-react';
import type { FloorPlanSaveConflict } from '@peles/shared';
import { useDialog } from '../../context/DialogContext';
import { formatSavedAt } from './saveStatus';

interface SaveConflictBannerProps {
  conflict: FloorPlanSaveConflict;
  /** Throw this canvas away and load the version that was saved. */
  onLoadTheirs: () => void | Promise<void>;
  /** Write this canvas over the version that was saved. */
  onKeepMine: () => void | Promise<unknown>;
  busy?: boolean;
}

/**
 * Shown when a save was refused because the plan was saved elsewhere after
 * this copy was loaded. It stays until the producer chooses, and autosave
 * waits with it — a dialog that closed on Escape would have left the choice
 * unmade and the edits silently unsaved.
 */
export function SaveConflictBanner({
  conflict,
  onLoadTheirs,
  onKeepMine,
  busy,
}: SaveConflictBannerProps) {
  const { confirm } = useDialog();
  const who = conflict.updatedBy?.name ?? 'Someone else';

  const loadTheirs = async () => {
    const ok = await confirm({
      title: 'Load their version?',
      message: 'The changes you made here since your last save will be discarded.',
      confirmLabel: 'Discard mine',
    });
    if (ok) await onLoadTheirs();
  };

  const keepMine = async () => {
    const ok = await confirm({
      title: 'Keep your version?',
      message: `${who}'s changes will be replaced by what is on your screen. They stay in the version history.`,
      confirmLabel: 'Keep mine',
      variant: 'warning',
    });
    if (ok) await onKeepMine();
  };

  return (
    <div
      role="alert"
      className="mb-3 flex flex-wrap items-center gap-3 rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900"
      data-testid="save-conflict-banner"
    >
      <AlertTriangle size={18} className="shrink-0 text-amber-600" aria-hidden="true" />
      <div className="min-w-0 flex-1">
        <p className="font-medium">
          {who} saved this floor plan at {formatSavedAt(conflict.updatedAt)}, after you opened it.
        </p>
        <p className="text-amber-800">Your changes are not saved yet. Autosave is paused.</p>
      </div>
      <div className="flex gap-2">
        <button
          type="button"
          onClick={loadTheirs}
          disabled={busy}
          className="rounded-md border border-amber-300 bg-white px-3 py-1.5 font-medium text-amber-900 hover:bg-amber-100 disabled:opacity-50"
          data-testid="conflict-load-theirs"
        >
          Load their version
        </button>
        <button
          type="button"
          onClick={keepMine}
          disabled={busy}
          className="rounded-md bg-amber-600 px-3 py-1.5 font-medium text-white hover:bg-amber-700 disabled:opacity-50"
          data-testid="conflict-keep-mine"
        >
          Keep mine
        </button>
      </div>
    </div>
  );
}
